import React, { useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";

// Compartido entre LicitacionDetail.jsx y Delegado.jsx. Iter243.
// `simulateFn(bajaPct) -> Promise<{score_economico, puntos_max, ...}>` se
// pasa desde el padre para que cada página pueda llamar a su propio
// endpoint (distinto shape de payload: lid en URL vs external_id en body).
export function CurvaEconomica({ kpi, curve, simulateFn }) {
  const [bajaPct, setBajaPct] = useState(10);
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const soportada = ["lineal_inversa", "proporcional_inversa_oferta"].includes(kpi?.formula_economica_tipo) && kpi?.formula_economica_params?.puntos_max;

  const runSim = useCallback(async (v) => {
    setLoading(true);
    try {
      const r = await simulateFn(v);
      setSim(r);
    } catch { /* silent */ }
    setLoading(false);
  }, [simulateFn]);

  useEffect(() => { if (soportada) runSim(bajaPct); }, [soportada]); // eslint-disable-line

  if (!kpi) return null;
  if (!kpi.formula_economica_texto && kpi.formula_economica_tipo === "desconocida") {
    return <p className="text-xs text-gray-400">No se ha identificado ninguna fórmula económica en el pliego.</p>;
  }
  return (
    <div data-testid="resumen-curva-economica">
      {kpi.formula_economica_texto && (
        <p className="text-[11px] text-gray-500 italic mb-2 line-clamp-3">"{kpi.formula_economica_texto}"</p>
      )}
      {!soportada && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Fórmula detectada pero no soportada para simulación interactiva todavía (tipo: {kpi.formula_economica_tipo || "desconocida"}). Revisa el texto del pliego.
        </p>
      )}
      {soportada && (
        <>
          {curve?.length > 1 && (
            <div className="h-36 -mx-1">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={curve} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="baja_pct" tick={{ fontSize: 9 }} unit="%" />
                  <YAxis tick={{ fontSize: 9 }} />
                  <RTooltip formatter={(v) => [`${v} pts`, "Puntos"]} labelFormatter={(l) => `Baja ${l}%`} />
                  <Line type="monotone" dataKey="score" stroke="#007934" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 bg-[#E8F5E9]/60 border border-[#007934]/20 rounded-lg px-3 py-2">
            <label className="text-[11px] font-semibold text-gray-600 shrink-0">Simular tu baja:</label>
            <input type="range" min={0} max={50} step={0.5} value={bajaPct} data-testid="resumen-baja-slider"
              onChange={e => { const v = parseFloat(e.target.value); setBajaPct(v); runSim(v); }}
              className="flex-1 accent-[#007934]" />
            <span className="text-xs font-bold tabular-nums w-12 text-right">{bajaPct}%</span>
            <span className="text-sm font-bold text-[#007934] tabular-nums w-20 text-right" data-testid="resumen-score-economico">
              {loading ? "…" : sim?.score_economico != null ? `${sim.score_economico} pts` : "—"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default CurvaEconomica;
